import React, { useEffect, useState } from "react";
import {
  Zap, Plus, Pencil, Trash2, Mail, MessageSquare, Bell, Tag, Megaphone, CheckSquare,
  Clock, ArrowRight, Loader2, Sparkles, History, Play, X, Loader,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { automationsApi, pipelineApi, estimatesApi, jobsApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import AutomationBuilder from "../components/automations/AutomationBuilder";

const ACTION_META = {
  send_email: { label: "Email", icon: Mail, color: "text-blue-600 bg-blue-50" },
  send_sms: { label: "SMS", icon: MessageSquare, color: "text-emerald-600 bg-emerald-50" },
  notify_team: { label: "Notify", icon: Bell, color: "text-amber-600 bg-amber-50" },
  set_tag: { label: "Tag", icon: Tag, color: "text-violet-600 bg-violet-50" },
  start_campaign: { label: "Campaign", icon: Megaphone, color: "text-pink-600 bg-pink-50" },
  create_task: { label: "Task", icon: CheckSquare, color: "text-slate-600 bg-slate-100" },
};

const SYSTEM_LABEL = { sales: "Sales", jobs: "Jobs" };

export default function Automations({ embedded = false }) {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const [rules, setRules] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [stageNames, setStageNames] = useState({}); // stageId -> name
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState(null); // {initial} | null
  const [runsFor, setRunsFor] = useState(null);
  const [testFor, setTestFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rs, rcps, pls] = await Promise.all([
        automationsApi.list(),
        automationsApi.recipes().catch(() => []),
        pipelineApi.pipelines().catch(() => []),
      ]);
      setRules(rs);
      setRecipes(rcps);
      setPipelines(pls);
      // resolve stage names across all referenced boards
      const boards = [...new Set(rs.map((r) => r.board))];
      const map = {};
      await Promise.all(
        boards.map(async (b) => {
          try {
            const st = await pipelineApi.stages(b);
            st.forEach((s) => (map[s.id] = s.name));
          } catch { /* ignore */ }
        })
      );
      setStageNames(map);
    } catch (err) {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => { await load(); })();
  }, []);

  const boardLabel = (b) => SYSTEM_LABEL[b] || pipelines.find((p) => p.id === b)?.name || "Pipeline";

  const toggle = async (rule, next) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: next } : r)));
    try {
      await automationsApi.update(rule.id, { enabled: next });
    } catch (err) {
      toast.error("Could not update");
      load();
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete the "${rule.name}" automation?`)) return;
    try {
      await automationsApi.remove(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      toast.success("Automation deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const startBlank = () => setBuilder({ initial: null });
  const startRecipe = (recipe) =>
    setBuilder({
      initial: {
        name: recipe.name,
        trigger: recipe.trigger,
        triggerConfig: recipe.triggerConfig || {},
        actions: recipe.actions || [],
        board: embedded ? "platform-sales" : "sales",
        stageId: "",
        enabled: true,
      },
    });

  usePageHeader({
    title: "Automations",
    subtitle: "Trigger actions when cards move through your pipelines",
    actions:
      isAdmin && (
        <Button onClick={startBlank} data-testid="new-automation-btn" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
          <Plus className="h-4 w-4 mr-1.5" /> New automation
        </Button>
      ),
  }, !embedded);

  return (
    <>
      {embedded && isAdmin && (
        <div className="flex justify-end mb-4">
          <Button onClick={startBlank} data-testid="new-automation-btn" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
            <Plus className="h-4 w-4 mr-1.5" /> New automation
          </Button>
        </div>
      )}

      <div className="p-6 space-y-6 max-w-5xl">
        {/* Recipes */}
        {isAdmin && recipes.length > 0 && (
          <div data-testid="recipes-section">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <h2 className="text-[13px] font-bold text-slate-800 uppercase tracking-wide">Starter recipes</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recipes.map((r) => (
                <button
                  key={r.key}
                  onClick={() => startRecipe(r)}
                  data-testid={`recipe-${r.key}`}
                  className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{r.category}</span>
                    <Plus className="h-4 w-4 text-slate-300 group-hover:text-blue-600" />
                  </div>
                  <div className="text-[13.5px] font-semibold text-slate-900">{r.name}</div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">{r.description}</div>
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    {(r.actions || []).map((a, i) => {
                      const m = ACTION_META[a.type];
                      if (!m) return null;
                      const Icon = m.icon;
                      return <span key={i} className={`inline-flex h-5 w-5 items-center justify-center rounded ${m.color}`}><Icon className="h-3 w-3" /></span>;
                    })}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active automations */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-blue-600" />
            <h2 className="text-[13px] font-bold text-slate-800 uppercase tracking-wide">Your automations</h2>
            <span className="text-[12px] text-slate-400">({rules.length})</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : rules.length === 0 ? (
            <div data-testid="automations-empty" className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
              <Zap className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <div className="text-[14px] font-semibold text-slate-600">No automations yet</div>
              <div className="text-[12.5px] text-slate-400 mt-1">Start from a recipe above or create one from scratch.</div>
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="automations-list">
              {rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  isAdmin={isAdmin}
                  boardLabel={boardLabel(rule.board)}
                  stageName={stageNames[rule.stageId] || "—"}
                  onToggle={(n) => toggle(rule, n)}
                  onEdit={() => setBuilder({ initial: rule })}
                  onDelete={() => remove(rule)}
                  onRuns={() => setRunsFor(rule)}
                  onTest={() => setTestFor(rule)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {builder && (
        <AutomationBuilder
          initial={builder.initial}
          embedded={embedded}
          onClose={() => setBuilder(null)}
          onSaved={() => {
            setBuilder(null);
            load();
            toast.success("Automation saved");
          }}
        />
      )}

      {runsFor && <RunsDrawer rule={runsFor} onClose={() => setRunsFor(null)} />}

      {testFor && <TestRunModal rule={testFor} boardLabel={boardLabel(testFor.board)} onClose={() => setTestFor(null)} />}
    </>
  );
}

function RuleRow({ rule, isAdmin, boardLabel, stageName, onToggle, onEdit, onDelete, onRuns, onTest }) {
  const triggerText =
    rule.trigger === "idle_in_stage"
      ? `Idle ${rule.triggerConfig?.idleHours || 24}h in`
      : "Enters";
  return (
    <div data-testid={`automation-row-${rule.id}`} className={`rounded-xl border bg-white p-4 transition-colors ${rule.enabled ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isAdmin ? (
              <button
                onClick={() => onEdit(rule)}
                data-testid={`automation-name-${rule.id}`}
                title="Click to edit"
                className="text-[14px] font-bold text-slate-900 truncate hover:text-blue-600 hover:underline text-left"
              >
                {rule.name}
              </button>
            ) : (
              <span className="text-[14px] font-bold text-slate-900 truncate">{rule.name}</span>
            )}
            {!rule.enabled && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">OFF</span>}
          </div>
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500 mt-1 flex-wrap">
            <span className="font-medium text-slate-600">{boardLabel}</span>
            <ArrowRight className="h-3 w-3 text-slate-300" />
            <span className="inline-flex items-center gap-1">
              {rule.trigger === "idle_in_stage" && <Clock className="h-3 w-3" />}
              {triggerText} <span className="font-medium text-slate-700">{stageName}</span>
            </span>
            <span className="text-slate-300">·</span>
            <div className="inline-flex items-center gap-1">
              {(rule.actions || []).map((a, i) => {
                const m = ACTION_META[a.type];
                if (!m) return null;
                const Icon = m.icon;
                return (
                  <span key={i} className={`inline-flex items-center gap-0.5 text-[10.5px] font-medium px-1.5 py-0.5 rounded ${m.color}`}>
                    <Icon className="h-3 w-3" />
                    {a.delayMinutes > 0 && <span className="opacity-70">{fmtDelay(a.delayMinutes)}</span>}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {isAdmin && (
          <button onClick={onTest} data-testid={`automation-test-${rule.id}`} title="Test run" className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/50">
            <Play className="h-3.5 w-3.5" /> Test run
          </button>
        )}
        <button onClick={onRuns} data-testid={`automation-runs-${rule.id}`} title="Run history" className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
          <History className="h-4 w-4" />
        </button>
        {isAdmin && (
          <>
            <button onClick={onEdit} data-testid={`automation-edit-${rule.id}`} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={onDelete} data-testid={`automation-delete-${rule.id}`} className="h-8 w-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
            <Switch checked={rule.enabled} onCheckedChange={onToggle} data-testid={`automation-toggle-${rule.id}`} />
          </>
        )}
      </div>
    </div>
  );
}

function RunsDrawer({ rule, onClose }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    automationsApi.runs(rule.id).then(setRuns).catch(() => setRuns([]));
  }, [rule.id]);
  const statusColor = (s) =>
    s === "done" ? "text-emerald-600 bg-emerald-50" : s === "failed" ? "text-rose-600 bg-rose-50" : s === "canceled" ? "text-slate-500 bg-slate-100" : "text-amber-600 bg-amber-50";
  return (
    <div className="fixed inset-0 z-[110] flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="runs-drawer">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[14px] font-semibold text-slate-900">Run history</div>
            <div className="text-[12px] text-slate-500">{rule.name}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">✕</button>
        </div>
        <div className="p-4 space-y-2">
          {runs === null ? (
            <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : runs.length === 0 ? (
            <div className="text-[12.5px] text-slate-400 text-center py-10">No runs yet. Actions appear here once triggered.</div>
          ) : (
            runs.map((r) => {
              const m = ACTION_META[r.actionType];
              const Icon = m?.icon || Zap;
              return (
                <div key={r.id} className="rounded-lg border border-slate-200 p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-700">
                      <Icon className="h-3.5 w-3.5" /> {m?.label || r.actionType}
                    </span>
                    <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded uppercase ${statusColor(r.status)}`}>{r.status}</span>
                  </div>
                  {r.result && <div className="text-[11px] text-slate-500 mt-1">{String(r.result)}</div>}
                  <div className="text-[10px] text-slate-300 mt-0.5">{r.executedAt || r.runAt}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDelay(m) {
  if (m % 1440 === 0) return `+${m / 1440}d`;
  if (m % 60 === 0) return `+${m / 60}h`;
  return `+${m}m`;
}

function TestRunModal({ rule, boardLabel, onClose }) {
  const [cards, setCards] = useState(null);
  const [cardId, setCardId] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCards = async () => {
      try {
        let list = [];
        if (rule.board === "sales") {
          list = (await estimatesApi.list()).map((e) => ({ id: e.id, label: `${e.id} · ${e.customer || "—"}` }));
        } else if (rule.board === "jobs") {
          list = (await jobsApi.list()).map((j) => ({ id: j.id, label: `${j.id} · ${j.customer || "—"}` }));
        } else {
          list = (await pipelineApi.cards(rule.board)).map((c) => ({ id: c.id, label: c.title + (c.customerName ? ` · ${c.customerName}` : "") }));
        }
        setCards(list);
        if (list[0]) setCardId(list[0].id);
      } catch {
        setCards([]);
      }
    };
    fetchCards();
  }, [rule.board]);

  const run = async () => {
    if (!cardId) return setError("Pick a card to test against");
    setRunning(true);
    setError("");
    try {
      const res = await automationsApi.testRun(rule.id, cardId);
      setResults(res.results || []);
      toast.success(`Ran ${res.ran} action(s)`);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Test run failed");
    } finally {
      setRunning(false);
    }
  };

  const statusColor = (s) =>
    s === "done" ? "text-emerald-600 bg-emerald-50" : s === "failed" ? "text-rose-600 bg-rose-50" : "text-slate-500 bg-slate-100";

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[88vh] overflow-y-auto" data-testid="test-run-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[14px] font-semibold text-slate-900 flex items-center gap-1.5"><Play className="h-4 w-4 text-blue-600" /> Test run</div>
            <div className="text-[12px] text-slate-500">{rule.name}</div>
          </div>
          <button onClick={onClose} data-testid="test-run-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <div className="text-[12px] text-slate-500">
            Runs every action <span className="font-semibold text-slate-700">immediately</span> (delays ignored) against a sample card from <span className="font-semibold text-slate-700">{boardLabel}</span>. Real emails/SMS will send if configured.
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Sample card</label>
            {cards === null ? (
              <div className="flex items-center gap-2 text-[12.5px] text-slate-400 py-2"><Loader className="h-4 w-4 animate-spin" /> Loading cards…</div>
            ) : cards.length === 0 ? (
              <div className="text-[12.5px] text-amber-600">No cards available on this board to test with.</div>
            ) : (
              <select value={cardId} onChange={(e) => setCardId(e.target.value)} data-testid="test-run-card" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                {cards.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            )}
          </div>

          {error && <div className="text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}

          {results && (
            <div className="space-y-2" data-testid="test-run-results">
              <div className="text-[12px] font-semibold text-slate-700">Results</div>
              {results.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-2.5 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-700">
                    {(() => { const m = ACTION_META[r.type]; const Icon = m?.icon || Zap; return <Icon className="h-3.5 w-3.5" />; })()}
                    {ACTION_META[r.type]?.label || r.type}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 max-w-[200px] truncate">{String(r.result || "")}</span>
                    <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded uppercase ${statusColor(r.status)}`}>{r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Close</Button>
          <Button onClick={run} disabled={running || !cardId} data-testid="test-run-go" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
            {running ? "Running…" : <><Play className="h-3.5 w-3.5 mr-1.5" /> Run now</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
